import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';

// Searchable brand picker with the ability to add a custom (unlisted) brand.
export const BrandCombobox = ({ value, onChange, options = [], placeholder = 'Select brand', testid }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const q = query.trim();
  const filtered = q
    ? options.filter((o) => o.toLowerCase().includes(q.toLowerCase()))
    : options;
  const exactExists = options.some((o) => o.toLowerCase() === q.toLowerCase());

  const choose = (val) => { onChange(val); setOpen(false); setQuery(''); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" role="combobox" aria-expanded={open} data-testid={testid}
          className="w-full flex items-center justify-between rounded-xl border border-white/15 bg-[#0A0A0A] px-3.5 py-2.5 text-sm text-left hover:border-white/25 transition-colors">
          <span className={value ? 'text-white' : 'text-muted-foreground'}>{value || placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-[#12161B] border-white/15 w-[--radix-popover-trigger-width] min-w-[220px]" align="start">
        <Command shouldFilter={false} className="bg-transparent">
          <CommandInput value={query} onValueChange={setQuery} placeholder={t('brandCombobox.search')} className="text-white" />
          <CommandList>
            {filtered.length === 0 && !q && <CommandEmpty>{t('brandCombobox.noBrands')}</CommandEmpty>}
            {q && !exactExists && (
              <CommandGroup heading={t('brandCombobox.addNew')}>
                <CommandItem value={`__add__${q}`} onSelect={() => choose(q)} className="text-[#48CAE4]" data-testid="brand-add-custom">
                  <Plus className="w-4 h-4 mr-2" /> {t('brandCombobox.add', { q })}
                </CommandItem>
              </CommandGroup>
            )}
            {filtered.length > 0 && (
              <CommandGroup heading={t('brandCombobox.brands')}>
                {filtered.map((brand) => (
                  <CommandItem key={brand} value={brand} onSelect={() => choose(brand)} className="text-white">
                    <Check className={`w-4 h-4 mr-2 ${value === brand ? 'opacity-100 text-[#00B4D8]' : 'opacity-0'}`} />
                    {brand}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
